import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { Inbox } from "lucide-react";
import { useMemo } from "react";
import { errorMessage } from "../../api/client";
import { demoRequestsQuery, ops, qk } from "../../api/ops";
import type { OpsDemoRequest } from "../../api/types";
import { DataTable } from "../../components/DataTable";
import { toast } from "../../components/toast";
import { Badge, Button, Card, EmptyState, ErrorState, Loading, PageHeader } from "../../components/ui";
import { fmtDateTime } from "../../lib/format";

export const Route = createFileRoute("/_app/demo-requests")({
  staticData: { crumb: { label: "Demo requests", parents: [] } },
  loader: ({ context }) => context.queryClient.prefetchQuery(demoRequestsQuery),
  component: DemoRequestsPage,
});

const col = createColumnHelper<OpsDemoRequest>();

/** A `mailto:` link to one address; encoded, so nothing in it adds a recipient or a subject. */
const mailto = (address: string) => `mailto:${encodeURIComponent(address.trim())}`;

function DemoRequestsPage() {
  const qc = useQueryClient();
  const list = useQuery(demoRequestsQuery);
  const mark = useMutation({
    mutationFn: ({ id, status }: { id: string; status: OpsDemoRequest["status"] }) =>
      ops.setDemoRequest(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.demoRequests }),
    onError: (e) => toast(errorMessage(e), "danger"),
  });

  const columns = useMemo(
    () => [
      col.accessor("created_at", {
        header: "Received",
        cell: (c) => <span className="whitespace-nowrap text-ink-2">{fmtDateTime(c.getValue())}</span>,
      }),
      col.accessor("name", {
        header: "Name",
        cell: (c) => (
          <span className="block">
            <span className="block font-medium">{c.getValue()}</span>
            <a className="block text-accent-ink text-caption" href={mailto(c.row.original.email)}>
              {c.row.original.email}
            </a>
          </span>
        ),
      }),
      col.accessor("company", { header: "Company" }),
      col.accessor((r) => r.team_size ?? "", { id: "team_size", header: "Team" }),
      col.accessor((r) => r.message ?? "", {
        id: "message",
        header: "Message",
        enableSorting: false,
        cell: (c) => (
          <span className="block max-w-[36ch] whitespace-pre-wrap text-ink-2">{c.getValue()}</span>
        ),
      }),
      col.accessor("status", {
        header: "Status",
        cell: (c) =>
          c.getValue() === "handled" ? <Badge tone="neutral">Handled</Badge> : <Badge tone="ok">New</Badge>,
      }),
      col.display({
        id: "actions",
        cell: (c) => {
          const r = c.row.original;
          const next = r.status === "handled" ? "new" : "handled";
          return (
            <div className="flex justify-end">
              <Button
                size="sm"
                disabled={mark.isPending}
                onClick={() => mark.mutate({ id: r.id, status: next })}
              >
                {next === "handled" ? "Mark handled" : "Reopen"}
              </Button>
            </div>
          );
        },
      }),
    ],
    [mark],
  );

  return (
    <>
      <PageHeader
        title="Demo requests"
        actions={
          list.data && list.data.new_count > 0 ? <Badge tone="ok">{list.data.new_count} new</Badge> : null
        }
      />
      <Card>
        {list.isPending ? (
          <Loading />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => list.refetch()} />
        ) : (
          <DataTable
            data={list.data.items}
            columns={columns}
            getRowId={(r) => r.id}
            initialSorting={[{ id: "created_at", desc: true }]}
            rowClassName={(r) => (r.status === "handled" ? "opacity-60" : undefined)}
            empty={<EmptyState icon={<Inbox />} title="No demo requests" />}
          />
        )}
      </Card>
    </>
  );
}
